const getUserAgent = (): string =>
  typeof navigator === 'undefined' ? '' : navigator.userAgent;

const isStandaloneWebApp = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const navigatorWithStandalone = navigator as Navigator & {
    standalone?: boolean;
  };
  return (
    navigatorWithStandalone.standalone === true ||
    (typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  );
};

export const isInstagramBrowser = (
  userAgent: string = getUserAgent()
): boolean => {
  // For reference on what Instagram's in-app browser uses for user agent:
  // https://whatmyuseragent.com/apps/instagram-app. For some reason it doesn't
  // seem to include iOS but I just checked the user agent w/in my Instagram app
  // on iOS and it also had "Instagram" in it.
  return /instagram/i.test(userAgent);
};

export const isTikTokBrowser = (
  userAgent: string = getUserAgent()
): boolean => {
  // For reference on what TikTok's in-app browser uses for user agent:
  // https://whatmyuseragent.com/apps/tiktok
  return /tiktok|bytedance|bytelocale/i.test(userAgent);
};

const isFacebookBrowser = (userAgent: string): boolean =>
  /\b(?:FBAN|FBAV|FB_IAB|FB4A)\b/i.test(userAgent);

const isGoogleAppBrowser = (userAgent: string): boolean =>
  /\bGSA\/[\d.]+/i.test(userAgent);

const isNamedEmbeddedBrowser = (userAgent: string): boolean =>
  isInstagramBrowser(userAgent) ||
  isTikTokBrowser(userAgent) ||
  isFacebookBrowser(userAgent) ||
  isGoogleAppBrowser(userAgent) ||
  /\b(?:TwitterAndroid|Twitter|Snapchat|Pinterest)\b/i.test(userAgent) ||
  /\bLine\/[\d.]+/i.test(userAgent);

const isAndroidWebView = (userAgent: string): boolean =>
  /Android/i.test(userAgent) &&
  (/;\s*wv\)/i.test(userAgent) ||
    /Version\/[\d.]+\s+Chrome\/[\d.]+.*Mobile\s+Safari/i.test(userAgent));

const isIosWebView = (
  userAgent: string,
  standalone: boolean = isStandaloneWebApp()
): boolean =>
  /(?:iPhone|iPad|iPod).*AppleWebKit.*Mobile/i.test(userAgent) &&
  !standalone &&
  !/(?:Safari|CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|Ddg|Brave)/i.test(
    userAgent
  );

/**
 * Detects high-confidence embedded and in-app browser user agents. These
 * clients can expose WebAuthn globals while still being unable to complete a
 * relying-party passkey ceremony, so capability checks must not rely on the
 * globals alone.
 */
export const isEmbeddedBrowser = (
  userAgent: string = getUserAgent()
): boolean =>
  isNamedEmbeddedBrowser(userAgent) ||
  isAndroidWebView(userAgent) ||
  isIosWebView(userAgent);

export const isAndroidUserAgent = (userAgent: string): boolean =>
  /Android/i.test(userAgent);

export const isAppleDevice = (userAgent: string): boolean =>
  /Macintosh|MacIntel|MacPPC|Mac68K|iPhone|iPad|iPod/i.test(userAgent);

/**
 * Whether signup surfaces should offer Apple Sign In.
 * Login keeps Apple available so existing Apple accounts can still authenticate
 * on Android (including Private Relay addresses).
 *
 * Defaults to true when navigator is unavailable so signup fails open to
 * showing Apple rather than hiding it by accident.
 */
export const supportsAppleSignIn = (
  userAgent: string = typeof navigator !== 'undefined'
    ? navigator.userAgent
    : ''
): boolean => !isAndroidUserAgent(userAgent);
